/**
 * Arthaus Frame Switcher
 * Phase: 4 (refactor)
 *
 * Upgrades the Framed | Print Only toggle (snippets/frame-switcher.liquid)
 * from plain navigation into an in-place, no-reload product swap using the
 * Shopify Section Rendering API:
 *
 *     GET /products/<handle>?section_id=<product-hero section id>
 *
 * The returned markup is the rendered product-hero section for the counterpart
 * product (its own gallery, options, price and switcher). We splice the
 * [data-product-hero] contents into the current page, re-init the PDP module,
 * and push the counterpart URL so the address bar + back/forward stay correct.
 *
 * Depends on window.ArthausPDP (assets/product-config.js) — loaded first.
 * Falls back to a normal navigation if anything goes wrong (the toggle is a
 * real <a href>, so no-JS / error paths still work).
 */
(function () {
  'use strict';

  var cache = {};

  function getHero() {
    return document.querySelector('[data-product-hero]');
  }

  function heroSectionId() {
    var hero = getHero();
    if (!hero) return null;
    var section = hero.closest('.shopify-section');
    if (!section || !section.id) return null;
    return section.id.replace(/^shopify-section-/, '');
  }

  function handleFromPath(path) {
    var m = (path || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function currentHandle() {
    var hero = getHero();
    var active = hero && hero.querySelector('[data-frame-tab][aria-selected="true"]');
    return active ? active.getAttribute('data-handle') : null;
  }

  function setLoading(on) {
    var sw = document.querySelector('[data-frame-switcher]');
    if (sw) sw.setAttribute('aria-busy', on ? 'true' : 'false');
    var hero = getHero();
    if (hero) hero.style.opacity = on ? '0.55' : '';
  }

  // Legacy: the counterpart is another PRODUCT, fetched by handle through the Section
  // Rendering API. Product System v2: both formats are the same product on two templates
  // (`?view=artwork-print`), and a section id is only valid within its own template, so
  // the tab's URL is fetched whole and the hero is lifted out of it.
  function fetchSection(handle, url, v2) {
    var key = v2 ? url : handle;
    if (cache[key]) return Promise.resolve(cache[key]);
    var target;
    if (v2) {
      target = url;
    } else {
      var sid = heroSectionId();
      if (!sid) return Promise.reject(new Error('no section id'));
      target = '/products/' + handle + '?section_id=' + encodeURIComponent(sid);
    }
    return fetch(target)
      .then(function (r) { if (!r.ok) throw new Error('section ' + r.status); return r.text(); })
      .then(function (html) { cache[key] = html; return html; });
  }

  var swapping = false;

  function swapTo(handle, url, options) {
    options = options || {};
    var hero = getHero();
    if (!hero || !handle) { window.location.href = url; return; }
    if (swapping) return;   // ignore taps while a swap is in flight — avoids request bursts
    swapping = true;

    setLoading(true);
    fetchSection(handle, url, options.v2)
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newHero = doc.querySelector('[data-product-hero]');
        if (!newHero) throw new Error('no hero in response');

        function commit() {
          hero.innerHTML = newHero.innerHTML;
          if (newHero.hasAttribute('data-v2-mode')) hero.setAttribute('data-v2-mode', newHero.getAttribute('data-v2-mode'));

          var newTitle = doc.querySelector('[data-product-config] h1');
          if (newTitle) document.title = newTitle.textContent.trim();

          if (window.ArthausPDP && window.ArthausPDP.init) window.ArthausPDP.init(hero);
          bindSwitcher();

          if (!options.skipPush) {
            history.pushState({ frameSwap: true, handle: handle }, '', url);
          }
          setLoading(false);
          swapping = false;
          // Injected <script>s don't execute via innerHTML, so section-level
          // enhancements (the mobile hero-fit in product-hero) must re-measure
          // against the freshly swapped DOM.
          window.dispatchEvent(new CustomEvent('pdp:swapped'));
        }

        // Warm the incoming hero image first so the swapped-in hero paints
        // immediately instead of flashing blank while it loads (the disappearing-
        // image symptom on slower mobile connections). Capped so a slow image
        // never blocks the swap.
        var ni = newHero.querySelector('#product-primary-image');
        var nsrc = ni && ni.getAttribute('src');
        if (nsrc) {
          var pre = new Image();
          var fired = false;
          var go = function () { if (fired) return; fired = true; commit(); };
          pre.onload = go;
          pre.onerror = go;
          // Mirror the hero's responsive attributes so we warm the same candidate
          // the swapped-in <img> will pick (not the 1200w fallback src).
          var nsrcset = ni.getAttribute('srcset');
          if (nsrcset) { pre.sizes = ni.getAttribute('sizes') || ''; pre.srcset = nsrcset; }
          pre.src = nsrc;
          if (pre.complete) go();
          setTimeout(go, 700);
        } else {
          commit();
        }
      })
      .catch(function () {
        // Robust fallback: just navigate.
        swapping = false;
        window.location.href = url;
      });
  }

  function bindSwitcher() {
    var sw = document.querySelector('[data-frame-switcher]');
    if (!sw) return;
    var mode = sw.getAttribute('data-current-mode');
    sw.querySelectorAll('[data-frame-tab]').forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        // Allow new-tab / modified clicks to behave normally.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        if (tab.getAttribute('data-frame-tab') === mode) return; // already active
        swapTo(tab.getAttribute('data-handle'), tab.getAttribute('href'), { v2: tab.hasAttribute('data-v2') });
      });
    });
  }

  window.addEventListener('popstate', function () {
    var handle = handleFromPath(window.location.pathname);
    if (!handle) return;
    // v2: the handle is the same in both formats — the format is the `view` parameter.
    var sw = document.querySelector('[data-frame-switcher]');
    var v2Tab = sw && sw.querySelector('[data-frame-tab][data-v2]');
    if (v2Tab) {
      var want = /[?&]view=artwork-print(&|$)/.test(window.location.search) ? 'print' : 'framed';
      if (want !== sw.getAttribute('data-current-mode')) {
        var tab = sw.querySelector('[data-frame-tab="' + want + '"]');
        if (tab) swapTo(handle, tab.getAttribute('href'), { skipPush: true, v2: true });
      }
      return;
    }
    if (handle !== currentHandle()) {
      swapTo(handle, window.location.pathname, { skipPush: true });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindSwitcher);
  } else {
    bindSwitcher();
  }
})();
